import { HTTPErrorResponse } from 'dy-frontend-http-repository/lib/data/types';
import { FlexMonthResource } from 'dy-frontend-http-repository/lib/modules/Subscription/resources';
import momentTz from 'moment-timezone';
import React, { useState } from 'react';

import { ToastUtils } from '@app/data/utils';
import { Button, Card, Divider, Intent } from '@blueprintjs/core';
import Flex from '@components/Flex';
import Heading from '@components/Heading';
import Overlay from '@components/Overlay';
import DevText from '@components/Text';
import { ModalProps } from '@modals/types';

import { cancelFlexMonth } from '../../store/effects';

// Cancellation cutoff is a Pacific-time instant; render it in that zone with the zone abbreviation
const PACIFIC_TIMEZONE = 'America/Los_Angeles';

export interface CancelFlexMonthModalProps {
    subscriptionId: ID;
    flexMonth: FlexMonthResource;
    refetchOnSuccess?: () => Promise<unknown>;
}

type Props = ModalProps<CancelFlexMonthModalProps>;

const CancelFlexMonthModal: React.FC<Props> = ({ closeModal, data }) => {
    const [isSubmitting, setIsSubmitting] = useState(false);

    if (!data) {
        closeModal?.();
        return null;
    }

    const handleCancelFlexMonth = async () => {
        setIsSubmitting(true);

        try {
            await cancelFlexMonth(data.subscriptionId);

            ToastUtils.showToast({
                message: 'Flex Month cancelled, billing will continue as usual',
                intent: Intent.SUCCESS,
            });

            if (data.refetchOnSuccess) {
                await data.refetchOnSuccess();
            }

            closeModal?.();
        } catch (e) {
            console.error(e);

            const response = (e as { response?: HTTPErrorResponse }).response;
            ToastUtils.showToast({
                message: response?.data?.message ?? 'Could not cancel Flex Month',
                intent: Intent.DANGER,
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Overlay isOpen onClose={closeModal}>
            <Card style={{ width: '600px' }}>
                <Flex className="mb-2" align="center" justify="space-between">
                    <Heading type="h4">Cancel Flex Month</Heading>
                    <Button minimal icon="cross" onClick={closeModal} />
                </Flex>

                <DevText className="mb-2">
                    The scheduled Flex Month will be cancelled and the subscription will keep billing as usual:
                </DevText>

                <div className="mb-1">
                    <DevText muted className="mb-small">
                        Scheduled pause start
                    </DevText>
                    <DevText>{momentTz(data.flexMonth.pause_start_date_pacific).format('D MMM, YYYY')}</DevText>
                </div>

                <div className="mb-2">
                    <DevText muted className="mb-small">
                        Scheduled billing resume
                    </DevText>
                    <DevText>{momentTz(data.flexMonth.resume_date_pacific).format('D MMM, YYYY')}</DevText>
                </div>

                <Divider className="mb-2" />

                <DevText muted className="mb-2">
                    Cancellation is only possible until{' '}
                    {momentTz.tz(data.flexMonth.cancellable_until, PACIFIC_TIMEZONE).format('D MMM, YYYY, HH:mm z')} (48
                    hours before the pause starts). The internal team will be notified about the cancellation by email.
                </DevText>

                <Flex justify="flex-end">
                    <Button className="mr-1" outlined onClick={closeModal} disabled={isSubmitting}>
                        Keep Flex Month
                    </Button>

                    <Button intent={Intent.DANGER} loading={isSubmitting} onClick={handleCancelFlexMonth}>
                        Cancel Flex Month
                    </Button>
                </Flex>
            </Card>
        </Overlay>
    );
};

export default CancelFlexMonthModal;
